import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import {
  AddinTabs,
  Constants,
  DomainRegisterDateDTO,
  FreeServiceResult,
  PersonalTrustedDomains,
  ResultInfo
} from "../../models/shared.model";
import { EvaluatorResult } from "../../models/evaluator-result";
import { EvaluatorService } from "../../services/evaluator.service";
import { Subscription } from "rxjs";
import { ScrollToSectionService } from "../../services/scrollToSection.service";
import { DomainService } from "../../services/domain.service";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { AddTrustedDomainDialog } from "../add-trusted-domain/add-trusted-domain.dialog";
import { TranslateService } from "@ngx-translate/core";
import { SettingsEnum } from "../../models/settings.model";
import { ToastrService } from "ngx-toastr";
import { IMPERSONATION_RISK_VALUES } from "../../models/constants";
import { SharedService } from "../../services/shared.service";
import { SettingsService } from "../../services/settings.service";


@Component({
  selector: "app-sender",
  templateUrl: "./sender.component.html",
  styleUrls: ["./sender.component.css"],
})
export class SenderComponent implements OnInit, OnChanges, OnDestroy {

  @Input("senderResult") senderResult: EvaluatorResult;
  @Input("riskResult") riskResult: EvaluatorResult;
  @Input("isRetrievingAge") isRetrievingAge = false;

  protected readonly Constants = Constants;

  addingDomainToTrust = false;
  addingEmailToTrust = false;

  domainsLimit = 0;
  personalTrustedDomains: PersonalTrustedDomains;
  trustDomainBtnTooltip = "";

  senderInfo: ResultInfo;
  riskInfo: ResultInfo;
  private scrollSubscription!: Subscription;

  constructor(
    private evaluatorService: EvaluatorService,
    private el: ElementRef,
    private scrollToSectionService: ScrollToSectionService,
    private translateService: TranslateService,
    private domainService: DomainService,
    public dialog: MatDialog,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private sharedService: SharedService,
    private settingsService: SettingsService,
  ) {
  }

  ngOnInit(): void {
    this.scrollSubscription = this.scrollToSectionService.scrollToSection$.subscribe((scrollTo) => {
      if(scrollTo.tab == AddinTabs.SENDER)
        this.scrollToSection(scrollTo.id);
    });

    this.loadPersonalTrustedList();

    this.domainService.reloadPersonalTrustedList.subscribe(value => {
      if (value)
        this.loadPersonalTrustedList();
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["senderResult"]?.currentValue) {
      this.senderResult = changes["senderResult"].currentValue;
      this.senderInfo = this.senderResult.additionalInfo;
    }

    if (changes["riskResult"]?.currentValue) {
      this.senderResult = changes["riskResult"].currentValue;
      this.riskInfo = this.riskResult.additionalInfo;
    }
  }

  loadPersonalTrustedList(){
    this.personalTrustedDomains = this.domainService.loadPersonalTrustedListFromCache();
  }

  async retrieveDomainAge() {
    this.isRetrievingAge = true;

    await this.evaluatorService.checkAge().then(value => this.isRetrievingAge = false)
  }

  getDomainAgeColor(isDomainNew: boolean | undefined) {
    if(isDomainNew == undefined)
      return 'text-orange-color';
    if(isDomainNew)
      return 'red';
    return 'text-green-2-color';
  }

  getTldCategoryColor(){
    switch (this.senderInfo?.topLevelDomainCategory){
      case Constants.DANGEROUS_DOMAIN:
        return 'red';
      case Constants.STANDARD_DOMAIN:
        return 'text-orange-color';
      default:
        return 'text-green-2-color'
    }
  }

  getTrustedColor(){
    return (this.senderInfo?.isDomainTrusted || this.senderInfo?.isSenderEmailInPersonalTrusted) ? 'text-green-2-color': 'red'
  }

  getExchangeHistoryColor(count: any){
    if(count == 'Pending')
      return 'text-orange-color';

    if(count > 3)
      return 'text-green-2-color'

    return 'red';
  }

  showCheckBtn() {
    const cases = [Constants.TRUSTED_DOMAIN.toString(), Constants.FAILED_DOMAIN_AGE.toString(), Constants.FREE_SERVICE.toString(),
      Constants.NOT_AVAILABLE_DOMAIN_AGE.toString()];

    return this.senderInfo?.domainAge == undefined || cases.includes(this.senderInfo?.domainAge);
  }

  scrollToSection(sectionId: string) {
    if(sectionId == '')
      return;

    const sectionElement = this.el.nativeElement.querySelector(`#${sectionId}`);

    if (sectionElement) {
      setTimeout(()=>{
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.scrollToSectionService.applyHighlight(sectionElement);
      }, 200)
    }
  }

  checkIfMatchSender(valueToCheck: boolean | undefined) {
    const word = this.translateService.instant(valueToCheck ? 'shared.matches' : 'shared.does_not_match');
    const sender = this.translateService.instant('sender.sender');

    return word + " " + sender;
  }

  checkSecurityProtocols(valueToCheck: string | undefined, isDkim?: boolean): string {
    if (valueToCheck === undefined) {
      return this.translateService.instant('shared.internal');
    }

    if (valueToCheck === 'none') {
      valueToCheck = isDkim ? 'none_dkim' : 'none';
    }

    const translations = new Map<string, string>([
      [IMPERSONATION_RISK_VALUES.PASS, 'shared.pass'],
      [IMPERSONATION_RISK_VALUES.BEST_GUESS_PASS, 'shared.pass'],
      [IMPERSONATION_RISK_VALUES.FAIL, 'shared.fail'],
      [IMPERSONATION_RISK_VALUES.SOFT_FAIL, 'shared.soft_fail'],
      [IMPERSONATION_RISK_VALUES.NEUTRAL, 'shared.neutral'],
      [IMPERSONATION_RISK_VALUES.NONE, 'shared.none'],
      ['none_dkim', 'shared.none_dkim'], // didn't change it because it is a special case
      [IMPERSONATION_RISK_VALUES.TEMP_ERROR, 'shared.temp_error'],
      [IMPERSONATION_RISK_VALUES.PERM_ERROR, 'shared.perm_error'],
    ]);

    return this.translateService.instant(translations.get(valueToCheck) ?? 'shared.unknown');
  }


  isTrustBtnHidden(): boolean {
    return this.senderInfo?.isDomainTrusted && !this.senderInfo?.isDomainPersonalTrusted;
  }

  isAllowedToTrust(isDomain: boolean): boolean {
    const topLevelDomain = this.senderInfo?.topLevelDomain?.toLowerCase();

    if (!topLevelDomain)
      return false;

    const allowTrustDomains = this.settingsService.getSettingValue(SettingsEnum.ALLOW_USERS_TO_TRUST_DOMAINS);

    if (allowTrustDomains !== true)
      return false;

    const freeEmailServices: FreeServiceResult[] = this.domainService.getFreeEmailServices();

    let freeService: FreeServiceResult = freeEmailServices.find(e => e.domain === this.senderInfo?.domain.toLowerCase());

    if(freeService && isDomain)
      return false;

    const dangerousTLDs = this.domainService.getDomains(true, false);
    const isDangerousTLD = dangerousTLDs.some(d => d.domain.toLowerCase() === topLevelDomain);

    if (isDangerousTLD) {
      const allowTrustDangerousTLD = this.settingsService.getSettingValue(SettingsEnum.ALLOW_USERS_TO_TRUST_DANGEROUS_TOP_LEVEL_DOMAIN);
      return allowTrustDangerousTLD === true;
    }

    return true;
  }

  async addToTrustList(isDomain: boolean) {

    let domain: DomainRegisterDateDTO = {
      domain_name: isDomain ? this.senderInfo?.domain.toLowerCase() : this.senderInfo?.sender.toLowerCase(),
      created_at: isDomain ? this.senderInfo?.domainCreatedAt : null,
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      data: { ...domain },
      isDomain: isDomain,
    };

    dialogConfig.width = '90%';
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(AddTrustedDomainDialog, dialogConfig);

    dialogRef.afterClosed().subscribe(async result => {
      if (!result)
        return;

      if(isDomain)
        this.addingDomainToTrust = true;
      else
        this.addingEmailToTrust = true;

      this.domainService.addDomainToTrustedList(result)
        .then(async result => {
          if(result && result.ok) {
            await this.successPersonalTrustOperation('trusted_domains.success_to_trust', domain.domain_name);
          }
          else{
            this.toastrService.error(this.translate.instant('trusted_domains.failed_to_trust', {domain: domain.domain_name}));
            console.log(result);
          }
        })
        .catch(error => {
          this.toastrService.error(error?.error ?? '', this.translate.instant('trusted_domains.failed_to_trust', {domain: domain.domain_name}));
          console.log(error);
        })
        .finally(() => {
          this.addingDomainToTrust = false;
          this.addingEmailToTrust = false;
        })
    });
  }

  removeFromTrustList(isDomain: boolean) {
    if(isDomain)
      this.addingDomainToTrust = true;
    else
      this.addingEmailToTrust = true;

    const domain = isDomain ? this.senderInfo?.domain : this.senderInfo?.sender;
    this.domainService.untrustDomain(domain, isDomain).then(async () => {
      await this.successPersonalTrustOperation('trusted_domains.success_to_untrust', domain);
    }).catch((error) => {
      console.error(error);
      this.toastrService.error(this.translate.instant("trusted_domains.untrusted_failed", { domain: domain }));
    })
    .finally(() => {
      this.addingDomainToTrust = false;
      this.addingEmailToTrust = false;
    })
  }

  async successPersonalTrustOperation(message: string, domain: string){
    await this.domainService.fetchPersonalTrustedList();
    this.domainService.reloadPersonalTrustedList.next(true);
    this.toastrService.success(this.translate.instant(message, { domain: domain }));
    this.sharedService.itemChanged.next(true);
  }

  isDomainInPersonalTrustedList(isDomain: boolean): boolean {
    if(isDomain)
      return this.personalTrustedDomains.domains.has(this.senderInfo?.domain.toLowerCase());

    return this.personalTrustedDomains.emails.has(this.senderInfo?.sender.toLowerCase());
  }

  getProtocolClass(status: string | undefined): string {
    const statusClasses: Partial<Record<IMPERSONATION_RISK_VALUES,string>> = {
      [IMPERSONATION_RISK_VALUES.PASS]: 'text-green-2-color',
      [IMPERSONATION_RISK_VALUES.PERM_ERROR]: 'text-orange-color',
      [IMPERSONATION_RISK_VALUES.TEMP_ERROR]: 'text-orange-color',
      [IMPERSONATION_RISK_VALUES.SOFT_FAIL]: 'text-orange-color',
      [IMPERSONATION_RISK_VALUES.FAIL]: 'red',
      [IMPERSONATION_RISK_VALUES.NONE]: 'text-black',
      [IMPERSONATION_RISK_VALUES.NEUTRAL]: 'text-black'
    };

    return status ? statusClasses[status as IMPERSONATION_RISK_VALUES] || '' : '';
  }

  ngOnDestroy() {
    this.scrollSubscription.unsubscribe()
  }

  getIsInTrustedListText() {
    let res = '';

    if(this.senderInfo?.isDomainTrusted)
      res = 'shared.yes';
    else
      res = this.senderInfo?.isSenderEmailInPersonalTrusted ? 'shared.trusted_sender' : 'shared.no';

    return this.translateService.instant(res);
  }
}


